import React, { useState, useEffect } from 'react';
import { X, Search, CreditCard, Receipt, Wallet, ChevronRight, FileText } from 'lucide-react';
import { getBookings } from '../lib/bookings';
import { createPayment, Payment } from '../lib/payments';
import { useBusiness } from '../contexts/BusinessContext';
import type { Booking } from '../lib/bookings';
import PaymentDetailsModal from './PaymentDetailsModal';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const paymentMethods = [
  {
    id: 'credit_card',
    name: 'Credit Card',
    icon: <CreditCard className="h-5 w-5 text-gray-400" />,
    description: 'Pay with credit or debit card'
  },
  {
    id: 'bank_transfer',
    name: 'Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-gray-400" />,
    description: 'Direct bank transfer'
  },
  {
    id: 'cash',
    name: 'Cash',
    icon: <Wallet className="h-5 w-5 text-gray-400" />,
    description: 'Pay with cash'
  }
];

export default function PaymentModal({ isOpen, onClose, onSuccess }: PaymentModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Load bookings when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadBookings();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadBookings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getBookings(selectedBusiness.id);
      setBookings(data);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings based on search query
  const filteredBookings = bookings.filter(booking => {
    if (!searchQuery) return false;

    const search = searchQuery.toLowerCase();
    const customerName = `${booking.customer?.first_name} ${booking.customer?.last_name}`.toLowerCase();
    const bookingNumber = String(booking.booking_number).toLowerCase();
    const bookingTitle = booking.title?.toLowerCase() || '';
    const vehicleInfo = booking.services?.map(service => 
      `${service.vehicle_make} ${service.vehicle_model}`.toLowerCase()
    ).join(' ') || '';

    return (
      customerName.includes(search) ||
      bookingNumber.includes(search) ||
      bookingTitle.includes(search) ||
      vehicleInfo.includes(search)
    );
  });

  const handleSubmit = async () => {
    if (!selectedBusiness?.id || !selectedBooking || !amount) return;

    try {
      setSubmitting(true);
      setError(null);

      // Validate amount
      const paymentAmount = parseFloat(amount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      // Calculate remaining balance with proper decimal handling for currency
      const totalPaid = selectedBooking.payments?.reduce((sum, p) => 
        p.status === 'completed' ? sum + (p.amount || 0) : sum,
        0
      ) || 0;
      // Round all values to 2 decimal places to avoid floating point precision issues
      const remainingBalance = Math.round((selectedBooking.total_amount - totalPaid) * 100) / 100;
      const roundedPaymentAmount = Math.round(paymentAmount * 100) / 100;

      // Validate payment amount
      if (roundedPaymentAmount > remainingBalance) {
        throw new Error(`Payment amount cannot exceed remaining balance of $${remainingBalance.toFixed(2)}`);
      }

      // Create the payment
      const paymentData = {
        business_id: selectedBusiness.id,
        booking_id: selectedBooking.id,
        amount: paymentAmount,
        method: selectedMethod.id as 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other',
        status: 'completed' as const,
        reference: reference || null,
        notes: notes || null,
        booking_number: selectedBooking.booking_number,
        booking_title: selectedBooking.title,
        booking_total: selectedBooking.total_amount,
        booking_subtotal: selectedBooking.subtotal,
        booking_tax_amount: selectedBooking.tax_amount,
        customer_first_name: selectedBooking.customer?.first_name || null,
        customer_last_name: selectedBooking.customer?.last_name || null,
        customer_email: selectedBooking.customer?.email || null,
        booking_services: selectedBooking.services?.map(service => ({
          id: service.id,
          name: service.name,
          description: service.description,
          price: service.price,
          tax_amount: service.tax_amount,
          total_amount: service.total_amount,
          duration_minutes: service.duration_minutes,
          vehicle_make: service.vehicle_make || '',
          vehicle_model: service.vehicle_model || ''
        })),
        booking_discounts: selectedBooking.discounts?.map(discount => ({
          id: discount.id,
          title: discount.title,
          type: discount.type as 'fixed' | 'percentage',
          amount: discount.amount
        }))
      } satisfies Omit<Payment, 'id' | 'created_at' | 'updated_at'>;

      const newPayment = await createPayment(paymentData);

      // Update the selected booking's payments array
      setSelectedBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          payments: [...(prev.payments || []), newPayment]
        };
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedBooking(null);
      setAmount('');
      setReference('');
      setNotes('');
    }
  };

  const handleViewInvoice = async () => {
    if (!selectedBooking) return;

    try {
      setIsLoadingInvoice(true);
      
      // Calculate total paid amount and payment status
      const totalPaid = selectedBooking.payments?.reduce((sum, p) => 
        p.status === 'completed' ? sum + (p.amount || 0) : sum, 0) || 0;
      const isPaid = totalPaid >= selectedBooking.total_amount;
      
      // If there are completed payments, use the most recent one
      const completedPayments = selectedBooking.payments?.filter(p => p.status === 'completed') || [];
      if (completedPayments.length > 0) {
        // Sort payments by created_at date in descending order
        const sortedPayments = [...completedPayments].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const latestPayment = sortedPayments[0];
        const paymentWithDetails: Payment = {
          id: latestPayment.id,
          business_id: selectedBooking.business_id,
          booking_id: selectedBooking.id,
          amount: latestPayment.amount,
          method: latestPayment.method || 'cash',
          status: latestPayment.status as 'completed' | 'pending' | 'failed' | 'refunded' | 'voided',
          reference: latestPayment.reference || '',
          notes: latestPayment.notes || '',
          created_at: latestPayment.created_at,
          updated_at: latestPayment.updated_at,
          booking_number: selectedBooking.booking_number,
          booking_title: selectedBooking.title,
          booking_total: selectedBooking.total_amount,
          customer_first_name: selectedBooking.customer?.first_name || '',
          customer_last_name: selectedBooking.customer?.last_name || '',
          customer_email: selectedBooking.customer?.email || '',
          booking_services: selectedBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: selectedBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
        setSelectedPayment(paymentWithDetails);
      } else {
        // Create a temporary payment object for unpaid bookings
        const tempPayment: Payment = {
          id: 'pending',
          business_id: selectedBooking.business_id,
          booking_id: selectedBooking.id,
          amount: selectedBooking.total_amount,
          method: 'cash',
          status: 'pending',
          reference: '',
          notes: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          booking_number: selectedBooking.booking_number,
          booking_title: selectedBooking.title,
          booking_total: selectedBooking.total_amount,
          customer_first_name: selectedBooking.customer?.first_name || '',
          customer_last_name: selectedBooking.customer?.last_name || '',
          customer_email: selectedBooking.customer?.email || '',
          booking_services: selectedBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: selectedBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
        setSelectedPayment(tempPayment);
      }
      
      setIsPaymentDetailsModalOpen(true);
    } catch (err) {
      console.error('Error preparing invoice:', err);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  // Calculate payment summary for selected booking
  const paymentSummary = selectedBooking ? {
    totalAmount: selectedBooking.total_amount,
    amountPaid: selectedBooking.payments?.reduce((sum, p) => 
      // Only count payments that are completed and not voided
      p.status === 'completed' ? sum + (p.amount || 0) : sum, 
      0
    ) || 0,
    get balanceDue() {
      return this.totalAmount - this.amountPaid;
    }
  } : null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[10000]`}
        onClick={onClose}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Record Payment</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-8">
                {/* Booking Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Select Booking
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by booking #, title, vehicle or customer..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  {/* Search Results */}
                  {searchQuery && (
                    <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          Loading bookings...
                        </div>
                      ) : error ? (
                        <div className="p-4 text-center text-sm text-red-600">
                          {error}
                        </div>
                      ) : filteredBookings.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          No bookings found
                        </div>
                      ) : (
                        filteredBookings.map((booking) => (
                          <button
                            key={booking.id}
                            onClick={() => {
                              setSelectedBooking(booking);
                              setSearchQuery('');
                              // Set default amount to remaining balance, excluding voided payments
                              const totalPaid = booking.payments?.reduce((sum, p) => 
                                p.status === 'completed' ? sum + (p.amount || 0) : sum,
                                0
                              ) || 0;
                              // Use proper decimal handling for currency calculations
                              const remainingBalance = Math.round((booking.total_amount - totalPaid) * 100) / 100;
                              if (remainingBalance > 0) {
                                setAmount(remainingBalance.toFixed(2));
                              }
                            }}
                            className="w-full p-3 text-left hover:bg-gray-50 flex items-center justify-between"
                          >
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                #{booking.booking_number} - {booking.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {booking.customer?.first_name} {booking.customer?.last_name}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {booking.services?.map((service, i) => (
                                  <span key={i}>
                                    {i > 0 && ' • '}
                                    {service.vehicle_make} {service.vehicle_model}
                                  </span>
                                ))}
                              </div>
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  booking.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {booking.status?.replace('_', ' ').charAt(0).toUpperCase() + booking.status?.replace('_', ' ').slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              ${booking.total_amount.toLocaleString()}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* Selected Booking Payment Summary */}
                  {selectedBooking && paymentSummary && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{selectedBooking.booking_number} - {selectedBooking.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {selectedBooking.customer?.first_name} {selectedBooking.customer?.last_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedBooking.services?.map((service, i) => (
                              <span key={i}>
                                {i > 0 && ' • '}
                                {service.vehicle_make} {service.vehicle_model}
                              </span>
                            ))}
                          </div>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              selectedBooking.status === 'completed' ? 'bg-green-100 text-green-800' :
                              selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              selectedBooking.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {selectedBooking.status?.replace('_', ' ').charAt(0).toUpperCase() + selectedBooking.status?.replace('_', ' ').slice(1)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={handleViewInvoice}
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          disabled={isLoadingInvoice}
                        >
                          <FileText className="h-4 w-4 mr-1.5" />
                          {isLoadingInvoice ? 'Loading...' : 'View Invoice'}
                        </button>
                      </div>

                      {/* Payment Summary */}
                      <div className="border-t border-gray-200 pt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Total Amount:</span>
                          <span className="font-medium">${paymentSummary.totalAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Amount Paid:</span>
                          <span className="font-medium">${paymentSummary.amountPaid.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium">
                          <span className="text-gray-900">Balance Due:</span>
                          <span className="text-gray-900">${paymentSummary.balanceDue.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Payment Method
                  </label>
                  <div className="space-y-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setSelectedMethod(method)}
                        className={`w-full flex items-center p-4 border rounded-lg ${
                          selectedMethod.id === method.id
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                          {method.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-gray-900">{method.name}</div>
                          <div className="text-sm text-gray-500">{method.description}</div>
                        </div>
                        {selectedMethod.id === method.id && (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-6">Payment Details</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Reference Number
                      </label>
                      <input
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter reference number"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm p-3"
                        placeholder="Add any additional notes about this payment..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              {error && (
                <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-md">
                  {error}
                </div>
              )}
              <button 
                onClick={handleSubmit}
                disabled={!selectedBooking || !amount || parseFloat(amount) <= 0 || submitting}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Recording Payment...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PaymentDetailsModal
        isOpen={isPaymentDetailsModalOpen}
        onClose={() => {
          setIsPaymentDetailsModalOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
        onUpdate={async () => {
          await loadBookings();
          onSuccess?.();
        }}
      />
    </>
  );
}